import { useEffect } from "react";
import { Heart, Stethoscope, Shield, Brain, AlertCircle, Wallet, Building, Briefcase, HardHat, HeartPulse } from "lucide-react";
import LeadForm from "../components/LeadForm";
import { SectionEyebrow, FadeInSection, PartnerStrip } from "../components/SharedSections";

const GroupBenefitsPage = () => {
  useEffect(() => {
    document.title = "Group Benefits BC | Small Business Benefits Plans | Mayker Financial";
    const meta = document.querySelector('meta[name="description"]');
    if (meta) {
      meta.setAttribute("content", "Custom group benefits plans for BC businesses of all sizes. Partners with iA Financial, Canada Life, and AGA Benefit Solutions.");
    }
    window.scrollTo(0, 0);
  }, []);

  const benefits = [
    { icon: <Stethoscope className="w-5 h-5" />, title: "Extended Health Care", desc: "Prescription drugs, paramedical, vision, and more" },
    { icon: <Heart className="w-5 h-5" />, title: "Dental Coverage", desc: "Preventive, basic, and major dental for your whole team" },
    { icon: <Shield className="w-5 h-5" />, title: "Life & Disability Insurance", desc: "Income protection if your team can't work" },
    { icon: <Brain className="w-5 h-5" />, title: "Mental Health Support", desc: "EAP programs and mental wellness benefits" },
    { icon: <AlertCircle className="w-5 h-5" />, title: "Critical Illness", desc: "Lump sum coverage for serious illness diagnoses" },
    { icon: <Wallet className="w-5 h-5" />, title: "Flexible Spending Accounts", desc: "Let employees customize their own benefits" }
  ];

  const industries = [
    { icon: <Building className="w-5 h-5" />, name: "Startups & Small Business" },
    { icon: <Briefcase className="w-5 h-5" />, name: "Professional Services" },
    { icon: <HardHat className="w-5 h-5" />, name: "Trades & Construction" },
    { icon: <HeartPulse className="w-5 h-5" />, name: "Healthcare & Nonprofits" }
  ];

  const steps = [
    { num: "1", title: "Free needs assessment", desc: "We learn about your business, your team, and your budget. No obligation." },
    { num: "2", title: "We design your plan", desc: "We compare options across iA, Canada Life, and AGA to build a plan that maximizes value for your budget." },
    { num: "3", title: "Ongoing management", desc: "We don't disappear after setup. We handle renewals, changes, and employee questions all year round." }
  ];

  return (
    <div data-testid="group-benefits-page">
      {/* Hero */}
      <section className="bg-[#0C1B33] pt-28 lg:pt-32 pb-16 lg:pb-20 px-4 lg:px-12 relative">
        <div className="hero-bottom-line" />
        <div className="max-w-6xl mx-auto">
          <FadeInSection>
            <SectionEyebrow light>Group Benefits · British Columbia</SectionEyebrow>
            <h1 className="font-playfair text-3xl lg:text-5xl font-bold text-white leading-tight mb-5 max-w-3xl">
              Group benefits that <em className="italic text-[#C9A84C]">attract talent</em> and reward your team.
            </h1>
            <p className="text-base lg:text-lg text-white/60 leading-relaxed max-w-2xl">
              We design, implement, and manage custom group benefits plans for BC businesses of all sizes — from 2-person startups to 200-person companies. Backed by Canada's leading benefits providers.
            </p>
          </FadeInSection>
        </div>
      </section>

      {/* Partner Strip - Prominent */}
      <PartnerStrip prominent />

      {/* What's Included */}
      <section className="bg-[#FAF8F3] py-16 lg:py-20 px-4 lg:px-12">
        <div className="max-w-6xl mx-auto">
          <FadeInSection>
            <SectionEyebrow>Coverage options</SectionEyebrow>
            <h2 className="font-playfair text-2xl lg:text-4xl font-semibold text-[#0C1B33] leading-tight mb-10 lg:mb-12">
              Everything your team needs
            </h2>
          </FadeInSection>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {benefits.map((benefit, index) => (
              <FadeInSection key={index}>
                <div className="bg-white border border-black/5 rounded-lg p-5 h-full pillar-card">
                  <div className="w-10 h-10 bg-[#C9A84C]/10 rounded-lg flex items-center justify-center mb-3 text-[#C9A84C]">
                    {benefit.icon}
                  </div>
                  <h3 className="font-medium text-[#0C1B33] mb-1">{benefit.title}</h3>
                  <p className="text-sm text-[#6B7A99]">{benefit.desc}</p>
                </div>
              </FadeInSection>
            ))}
          </div>
        </div>
      </section>

      {/* Who We Serve */}
      <section className="bg-[#F0EDE4] py-16 lg:py-20 px-4 lg:px-12">
        <div className="max-w-6xl mx-auto">
          <FadeInSection>
            <SectionEyebrow>Who we help</SectionEyebrow>
            <h2 className="font-playfair text-2xl lg:text-4xl font-semibold text-[#0C1B33] leading-tight mb-4">
              Built for BC businesses of all sizes
            </h2>
            <p className="text-[15px] text-[#6B7A99] max-w-2xl leading-relaxed mb-10">
              We work with businesses from 2 employees to 200+. Whether you're a startup offering benefits for the first time or an established company looking to upgrade your existing plan, we design a solution that fits your budget and your team's needs.
            </p>
          </FadeInSection>

          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            {industries.map((industry, index) => (
              <FadeInSection key={index}>
                <div className="bg-white border border-black/5 rounded-lg p-5 text-center">
                  <div className="w-10 h-10 bg-[#C9A84C]/10 rounded-lg flex items-center justify-center mb-3 text-[#C9A84C] mx-auto">
                    {industry.icon}
                  </div>
                  <h3 className="font-medium text-[#0C1B33] text-sm">{industry.name}</h3>
                </div>
              </FadeInSection>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="bg-[#0C1B33] py-16 lg:py-20 px-4 lg:px-12">
        <div className="max-w-6xl mx-auto">
          <FadeInSection>
            <SectionEyebrow light>Our process</SectionEyebrow>
            <h2 className="font-playfair text-2xl lg:text-4xl font-semibold text-white leading-tight mb-10 lg:mb-12">
              How we build your benefits plan
            </h2>
          </FadeInSection>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8">
            {steps.map((step, index) => (
              <FadeInSection key={index}>
                <div className="text-center">
                  <div className="step-number bg-[#C9A84C] text-[#0C1B33] mx-auto mb-4">
                    {step.num}
                  </div>
                  <h3 className="font-playfair text-lg font-semibold text-white mb-2">
                    {step.title}
                  </h3>
                  <p className="text-sm text-white/60 leading-relaxed">
                    {step.desc}
                  </p>
                </div>
              </FadeInSection>
            ))}
          </div>
        </div>
      </section>

      {/* Lead Form */}
      <section id="quote-form" className="bg-[#FAF8F3] py-16 lg:py-20 px-4 lg:px-12">
        <div className="max-w-xl mx-auto">
          <FadeInSection>
            <div className="bg-white rounded-xl p-6 lg:p-8 border border-black/5 card-top-accent">
              <LeadForm 
                phoneRequired
                title="Get a free group benefits quote"
                subtitle="Tell us about your business and we'll come back with a tailored recommendation."
                showCompanyFields={true}
              />
            </div>
          </FadeInSection>
        </div>
      </section>
    </div>
  );
};

export default GroupBenefitsPage;
